import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import nwAnalytics from '@nerdwallet/analytics';
import {
  Impression,
  ImpressionContext,
} from '@nerdwallet/analytics/react/impression';
import appConfig from '@nerdwallet/app-config';
import classes from '@nerdwallet/base-styles/classes';
import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { CenteredContainer } from '@nerdwallet/currency/components/CenteredContainer';
import { Cluster } from '@nerdwallet/currency/components/Cluster';
import { Stack } from '@nerdwallet/currency/components/Stack';
import { Text } from '@nerdwallet/currency/components/Text';
import { Title } from '@nerdwallet/currency/components/Title';

import useAnalytics from '~/client/lib/segment/useAnalytics';
import usePageViewId from '~/client/lib/segment/usePageViewId';
import SegmentImpression from '../../segment-impression';
import styles from './rewards-module.module.less';

const RewardsModule: React.FC = () => {
  const { trackSegmentTrackEvent } = useAnalytics();
  const pageViewId = usePageViewId();
  const [segmentImpressionId] = useState(uuidv4());
  const [ctaConfig, setCtaConfig] = useState<{
    destination?: string;
    text: string;
  }>();
  const [isDesktop, setIsDesktop] = useState<boolean>();

  useEffect(() => {
    const userAgent = navigator?.userAgent;

    setIsDesktop(userAgent?.indexOf('Mobi') === -1);

    // No link for mobile
    if (userAgent?.indexOf('Mobi') === -1) {
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/User-Agent#chrome_ua_string
      if (userAgent?.indexOf('Chrome') !== -1) {
        setCtaConfig({
          // destination: , // TODO uncomment and add real link (GROW-1228)
          text: "Add to Chrome - It's free!",
        });
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/User-Agent#microsoft_edge_ua_string
      } else if (userAgent?.indexOf('Edg') !== -1) {
        setCtaConfig({
          // destination: , // TODO uncomment and add real link (GROW-1228)
          text: "Add to Edge - It's free!",
        });
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/User-Agent#safari_ua_string
      } else if (userAgent?.indexOf('Safari') !== -1) {
        setCtaConfig({
          // destination: , // TODO uncomment and add real link (GROW-1228)
          text: "Add to Safari - It's free!",
        });
      }
    }
  }, []);

  return (
    <SegmentImpression
      eventName="Wildfire Shopping Rewards Banner Viewed"
      eventProps={{
        impression_id: segmentImpressionId,
        section_name: isDesktop ? 'desktop_homepage' : 'mobile_web_homepage',
      }}
    >
      <Impression
        eventType="element_impression"
        entityName="wildfire_shopping_rewards_banner"
        payload={{
          section_name: isDesktop ? 'desktop_homepage' : 'mobile_web_homepage',
        }}
      >
        <ImpressionContext.Consumer>
          {({ impressionId }) => (
            <Box backgroundColor="yellow-lightest" paddingTop="5">
              <CenteredContainer>
                <Box
                  className={styles.container}
                  display={{ desktop: 'grid', mobile: 'block' }}
                  gridGap="3"
                >
                  <Box className={styles.content} paddingBottom="5">
                    <Stack>
                      <Title size="small">
                        Shopping online just got nerdier
                      </Title>
                      <Text>
                        Get coupons and cash-back automatically applied when
                        you’re shopping online. All you need is a NerdWallet
                        account and our free browser extension.
                      </Text>
                      {ctaConfig ? (
                        <Button
                          href={ctaConfig.destination}
                          onClick={() => {
                            trackSegmentTrackEvent(
                              'Wildfire Shopping Rewards Banner CTA Clicked',
                              pageViewId,
                              {
                                parent_impression_id: segmentImpressionId,
                                section_name: 'desktop_homepage',
                              }
                            );
                            nwAnalytics.track('element_interacted', {
                              action: 'select',
                              entity_name:
                                'wildfire_shopping_rewards_banner_cta_clicked',
                              impression_id: impressionId,
                              interaction_type: 'press',
                            });
                          }}
                          primary
                        >
                          {ctaConfig.text}
                        </Button>
                      ) : (
                        <Text bold>
                          Available on desktop only, open this page on desktop
                          to download.
                        </Text>
                      )}
                      <Cluster>
                        <Box paddingLeft="3">
                          <Text light>Support:</Text>
                        </Box>
                        <img
                          alt="Chrome logo"
                          height={24}
                          src={`${appConfig.CDN_URL}/shopping-rewards-extension/chrome.png`}
                        />
                        <img
                          alt="Safari logo"
                          height={24}
                          src={`${appConfig.CDN_URL}/shopping-rewards-extension/safari.png`}
                        />
                        <img
                          alt="Edge logo"
                          height={24}
                          src={`${appConfig.CDN_URL}/shopping-rewards-extension/edge.png`}
                        />
                      </Cluster>
                    </Stack>
                  </Box>
                  <Box className={styles.image}>
                    <img
                      alt="illustration of a hand holding a phone displaying the NerdWallet logo with a house, watch, wrapped box, airplane, and sunglasses surrounding the phone"
                      className={classes('max-width-100')}
                      src={`${appConfig.CDN_URL}/shopping-rewards-extension/hero-background.png`}
                      style={{ marginBottom: '-6px' }}
                    />
                  </Box>
                </Box>
              </CenteredContainer>
            </Box>
          )}
        </ImpressionContext.Consumer>
      </Impression>
    </SegmentImpression>
  );
};

export default RewardsModule;
